import React, { useEffect, useState } from 'react';
import { formatDateTime, getRoleName, isNullOrEmptyString } from '../../services/UtilityService';
import CustomTable from '../../share-components/CustomTable';
import { getAdminAccounts } from '../../services/AccountService';
import { useNavigate } from 'react-router-dom';
import { ROUTE_ACCOUNT_CREATE } from '../../constants/routeConstants';
import { Button, Card, Container } from 'react-bootstrap';
import CommonDialog from '../../share-components/CommonDialog';
import * as Yup from 'yup';
import { useAppContext } from '../../share-components/AppContext';
import { ErrorMessage, Field, Form, Formik } from 'formik/dist';
import { resetPassword, toggleAccountActivate } from '../../services/AuthService';
import AlertDialog from '../../share-components/AlertDialog';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import FormTitle from '../../share-components/FormTitle';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';
import NoDataFound from '../../share-components/NoDataFound';

const AdminList = () => {
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [adminListData, setAdminListData] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showResetPasswordForm, setShowResetPasswordForm] = useState(false);
    const [showToggleActivateConfirmDialog, setShowToggleActivateConfirmDialog] = useState(false);
    const [actionTargetAccount, setActionTargetAccount] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const navigate = useNavigate();


    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const response = await getAdminAccounts() || [];
            if (response) {
                const tableData = response.map((account) => ({
                    ...account,
                    roleShow: getRoleName(account.role),
                    deactivateFlagShow: account.deactivateFlag === false ? "YES" : "NO",
                    createdDateTimeShow: formatDateTime(account.createdDateTime?.slice(0, 8)),
                    updatedDateTimeShow: formatDateTime(account.updatedDateTime?.slice(0, 8))
                }));
                setAdminListData(tableData);
            }
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setRouteNaviData("MASTER >> ADMIN LIST")
        fetchDashboard();
    }, []);

    const handleResetPasswordClick = (userAccount) => {
        setActionTargetAccount(userAccount);
        setShowResetPasswordForm(true);
    }

    const handleToggleActivateClick = (userAccount) => {
        setActionTargetAccount(userAccount);
        setShowToggleActivateConfirmDialog(true);
    }

    const columns = [
        {
            Header: 'Username',
            accessor: 'username'
        },
        {
            Header: 'Email',
            accessor: 'email'
        },
        {
            Header: 'Role',
            accessor: 'roleShow',
        },
        {
            Header: 'Active',
            accessor: 'deactivateFlagShow',
        },
        {
            Header: 'Created Date',
            accessor: 'createdDateTimeShow',
        },
        {
            Header: 'Updated Date',
            accessor: 'updatedDateTimeShow',
        },
        {
            Header: 'Reset Password',
            accessor: 'resetPassword',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            Cell: ({ row }) => (
                <Button variant='secondary'
                    onClick={() => handleResetPasswordClick(row.original)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-unlock-fill"></i>
                    Reset
                </Button>
            )
        },
        {
            Header: 'Activate/Deactivate',
            accessor: 'toggleActivate',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center',
            Cell: ({ row }) => (
                row.original.deactivateFlag === false ? <Button variant='danger'
                    onClick={() => handleToggleActivateClick(row.original)}
                    style={{
                        padding: "0px 5px",
                        margin: "5px"
                    }}
                >
                    <i className="bi bi-ban"></i>
                    Deactivate
                </Button> :
                    <Button variant='primary'
                        onClick={() => handleToggleActivateClick(row.original)}
                        style={{
                            padding: "0px 5px",
                            margin: "5px"
                        }}
                    >
                        <i className="bi bi-check2-circle"></i>
                        Activate
                    </Button>
            )
        }
    ];

    const goToCreateAccount = () => {
        navigate(ROUTE_ACCOUNT_CREATE);
    }

    const initialValues = {
        passkey: "",
        password: "",
        confirmPassword: ""
    };

    const validationSchema = Yup.object({
        passkey: Yup.string()
            .max(50, 'Passkey must not exceed 50 characters.')
            .required('Passkey is required.'),
        password: Yup.string()
            .max(50, 'Password must not exceed 50 characters.')
            .matches(/^[A-Za-z0-9!@#$%^&*()_+=-]+$/, 'Password can only contain alphabets, numbers, and special characters.')
            .required('Password is required.'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match.')
            .required('Confirm Password is required.')
    });

    const initialValuesForToggleActivate = {
        passkey: "",
    };

    const validationSchemaForToggleActivate = Yup.object({
        passkey: Yup.string()
            .max(50, 'Passkey must not exceed 50 characters.')
            .required('Passkey is required.'),
    });

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const onSubmit = async (form) => {
        setLoading(true);
        try {
            const requestData = {
                userId: actionTargetAccount?.userId,
                passkey: form.passkey,
                password: form.password
            }
            await resetPassword(requestData);
            setSuccessMessage("Account Password is Successfully Reseted.")
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActivate = async (form) => {
        setLoading(true);
        try {
            const requestData = {
                userId: actionTargetAccount?.userId,
                passkey: form.passkey,
            }
            await toggleAccountActivate(requestData);
            setSuccessMessage(`Account Password is Successfully ${actionTargetAccount?.deactivateFlag === false ? "Deactivated" : "Activated"}.`);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowResetPasswordForm(false);
        setShowToggleActivateConfirmDialog(false);
        setSuccessMessage(null);
        setActionTargetAccount(null);
        await fetchDashboard();
    }

    const renderPasswordReset = (
        <Container style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '30px' }}>
            <h5 className="text-center"><span >{actionTargetAccount?.username}</span></h5>
            <h5 className="text-center"><span >{actionTargetAccount?.email}</span></h5>

            {/* PasswordReset Form */}
            <Card style={{ width: '100%', maxWidth: '400px', padding: '20px', margin: '0 auto' }}>
                <Card.Body>
                    <FormTitle title={"Reset Account Password"} isCenter={true} />
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={onSubmit}
                        enableReinitialize={true}>
                        {({ errors, isValid, dirty }) => {
                            const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                            return (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Passkey*</label>
                                        <Field name="passkey" type={showPassword ? 'text' : 'password'} className="form-control customInputControl" />
                                        <ErrorMessage name="passkey" component="div" className="text-danger" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">New Password*</label>
                                        <Field name="password" type={showPassword ? 'text' : 'password'} className="form-control customInputControl" />
                                        <ErrorMessage name="password" component="div" className="text-danger" />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Confirm New Password*</label>
                                        <Field name="confirmPassword" type={showPassword ? 'text' : 'password'} className="form-control customInputControl" />
                                        <ErrorMessage name="confirmPassword" component="div" className="text-danger" />
                                    </div>
                                    {/* Show Password Checkbox */}
                                    <div className="mb-3">
                                        <label className='custom-label'>
                                            <input
                                                type="checkbox"
                                                checked={showPassword}
                                                onChange={togglePasswordVisibility}
                                            />
                                            &nbsp;Show Password
                                        </label>
                                    </div>

                                    <Button
                                        className="w-100 mb-3"
                                        type="submit"
                                        variant="primary"
                                        disabled={!(isValid && dirty)}
                                    >
                                        Reset Password
                                    </Button>
                                    <Button
                                        className="w-100 mb-3"
                                        type="button"
                                        variant="secondary"
                                        onClick={() => { setShowResetPasswordForm(false); setActionTargetAccount(null) }}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );

    const renderToggleActivate = (
        <Container style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingBottom: '30px' }}>
            <h4 className="text-center word-wrap"><span >{actionTargetAccount?.username}</span></h4>
            <h4 className="text-center word-wrap"><span >{actionTargetAccount?.email}</span></h4>

            {/* PasswordReset Form */}
            <Card style={{ width: '100%', maxWidth: '400px', padding: '20px', margin: '0 auto' }}>
                <Card.Body>
                    <FormTitle title={actionTargetAccount?.deactivateFlag === false ? "Deactivate" : "Activate"} isCenter={true} />
                    <Formik
                        initialValues={initialValuesForToggleActivate}
                        validationSchema={validationSchemaForToggleActivate}
                        onSubmit={handleToggleActivate}
                        enableReinitialize={true}>
                        {({ errors, isValid, dirty }) => {
                            const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                            return (
                                <Form>
                                    <div className="mb-3">
                                        <label className="form-label custom-label">Passkey*</label>
                                        <Field name="passkey" type={showPassword ? 'text' : 'password'} className="form-control customInputControl" />
                                        <ErrorMessage name="passkey" component="div" className="text-danger" />
                                    </div>
                                    {/* Show Password Checkbox */}
                                    <div className="mb-3">
                                        <label className='custom-label'>
                                            <input
                                                type="checkbox"
                                                checked={showPassword}
                                                onChange={togglePasswordVisibility}
                                            />
                                            &nbsp;Show Passkey
                                        </label>
                                    </div>

                                    <Button
                                        className="w-100 mb-3"
                                        type="submit"
                                        variant={actionTargetAccount?.deactivateFlag === false ? "danger" : "primary"}
                                        disabled={!(isValid && dirty)}
                                    >
                                        {actionTargetAccount?.deactivateFlag === false ? "Deactivate" : "Activate"}
                                    </Button>
                                    <Button
                                        className="w-100 mb-3"
                                        type="button"
                                        variant="secondary"
                                        onClick={() => { setShowToggleActivateConfirmDialog(false); setActionTargetAccount(null) }}
                                    >
                                        Cancel
                                    </Button>
                                </Form>
                            );
                        }}
                    </Formik>
                </Card.Body>
            </Card>
        </Container>
    );

    return (
        <div id='adminListPage' style={{ overflow: "auto", paddingBottom: "20px" }}>
            {isFirstLoaded && <div style={{ minWidth: "max-content", maxWidth: '650px', margin: '0 auto' }}>
                <FormTitle title={"Admin Accounts"} />
                <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                    <div style={{ display: 'inline-block' }}>
                        <Button type='button' variant='outline-secondary' className='btn-sm' onClick={goToCreateAccount}><i className="bi bi-plus-circle"></i> Add New Account</Button>
                    </div>

                    <div style={{ display: 'inline-block' }}>
                        <SearchColumnSelect data={adminListData} setData={setFilteredData} columns={columns} />
                    </div>

                </div>
                {filteredData?.length > 0 ?
                    <CustomTable
                        tableId='adminListTbl'
                        columns={columns}
                        tblData={filteredData || []}
                        showPagination={true}
                        itemsPerPage={20}
                        initialSortBy={[{ id: 'username', desc: false }]}
                    /> : <NoDataFound />}
                <CommonDialog
                    dialogId='passwordResetDialog'
                    show={showResetPasswordForm}
                    noCloseBtn={false}
                    onHide={() => { setShowResetPasswordForm(false); setActionTargetAccount(null); }}
                    size={'fullscreen'}
                    body={renderPasswordReset}
                />
                <CommonDialog
                    dialogId='toggleActivateDialog'
                    show={showToggleActivateConfirmDialog}
                    noCloseBtn={false}
                    onHide={() => { setShowToggleActivateConfirmDialog(false); setActionTargetAccount(null); }}
                    size={'fullscreen'}
                    body={renderToggleActivate}
                />
                <AlertDialog
                    dialogId={'successAlertDialog'}
                    showDialog={!isNullOrEmptyString(successMessage)}
                    body={successMessage}
                    onConfirm={handleSuccessDialogClose}
                />
            </div>}
        </div>
    );
};

export default AdminList;